import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import GenerateContractPDF from "../../components/fsm/GenerateContractPDF";

const API = '/api/v1/fsm';

const ContractDetail = () => {
  const { lang, t } = useLanguage();
  const f = t('fsm');
  const navigate = useNavigate();
  const { id } = useParams();
  const isRTL = lang === 'ar';

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const jsonHeaders = { ...headers, 'Content-Type': 'application/json' };

  // ── State ──
  const [contract, setContract] = useState(null);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [contractWOs, setContractWOs] = useState([]);
  const [woLoading, setWoLoading] = useState(false);
  const [maintenancePlans, setMaintenancePlans] = useState([]);
  const [statusAction, setStatusAction] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Equipment add modal
  const [showAddEquip, setShowAddEquip] = useState(false);
  const [availableEquipment, setAvailableEquipment] = useState([]);
  const [selectedNewEquip, setSelectedNewEquip] = useState([]);

  // ── Fetch Contract ──
  const fetchContract = useCallback(async () => {
    try {
      const res = await fetch(`${API}/contracts/${id}`, { headers });
      if (res.ok) {
        const data = await res.json();
        if (data.success) setContract(data.data);
      }
    } catch (err) { console.error('Fetch contract error:', err); }
  }, [id]);

  // ── Fetch Equipment ──
  const fetchEquipment = useCallback(async () => {
    try {
      const res = await fetch(`${API}/contracts/${id}/equipment`, { headers });
      if (res.ok) {
        const data = await res.json();
        if (data.success) setEquipment(data.data || []);
      }
    } catch (err) { console.error('Fetch equipment error:', err); }
  }, [id]);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchContract(), fetchEquipment()]);
      // Fetch maintenance plans for this contract
      try {
        const mpRes = await fetch(`${API}/maintenance-plans?contract_id=${id}`, { headers });
        const mpData = await mpRes.json();
        if (mpData.success) setMaintenancePlans(mpData.data || []);
      } catch (e) { console.error('Error fetching maintenance plans:', e); }
      setLoading(false);
    };
    loadAll();
  }, [fetchContract, fetchEquipment]);

  // ── Helpers ──
  const fmtDate = (d) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleDateString(lang === 'ar' ? 'ar-EG' : 'en-US', {
        year: 'numeric', month: 'short', day: 'numeric'
      });
    } catch { return d; }
  };

  const fmtCurrency = (v) => {
    if (!v && v !== 0) return '—';
    return Number(v).toLocaleString(lang === 'ar' ? 'ar-EG' : 'en-US');
  };

  const statusColors = {
    draft: { bg: 'rgba(107,114,128,0.2)', color: '#9ca3af', icon: '📝' },
    active: { bg: 'rgba(22,163,106,0.2)', color: '#4ade80', icon: '✅' },
    suspended: { bg: 'rgba(217,119,6,0.2)', color: '#fbbf24', icon: '⏸️' },
    expired: { bg: 'rgba(220,38,38,0.2)', color: '#f87171', icon: '❌' },
    cancelled: { bg: 'rgba(107,114,128,0.15)', color: '#6b7280', icon: '🚫' },
    renewed: { bg: 'rgba(37,99,235,0.2)', color: '#60a5fa', icon: '🔄' },
  };

  const statusLabel = (s) => f[`cst_${s}`] || s;
  const typeLabel = (t_) => f[`ctp_${t_}`] || t_;

  // ── Status Change ──
  const handleStatusChange = async (newStatus) => {
    if (!window.confirm(lang === 'ar'
      ? `هل تريد تغيير الحالة إلى ${statusLabel(newStatus)}؟`
      : `Change status to ${statusLabel(newStatus)}?`
    )) return;

    setActionLoading(true);
    try {
      const res = await fetch(`${API}/contracts/${id}/status`, {
        method: 'PUT', headers: jsonHeaders,
        body: JSON.stringify({ status: newStatus })
      });
      const data = await res.json();
      if (data.success) {
        fetchContract();
      } else {
        alert(data.message || 'Failed');
      }
    } catch (err) { alert('Network error'); }
    finally { setActionLoading(false); }
  };

  // ── Renew ──
  const handleRenew = async () => {
    if (!window.confirm(lang === 'ar' ? 'هل تريد تجديد العقد؟' : 'Renew this contract?')) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/contracts/${id}/renew`, {
        method: 'POST', headers: jsonHeaders, body: JSON.stringify({})
      });
      const data = await res.json();
      if (data.success && data.data?.id) {
        navigate(`/fsm/contracts/${data.data.id}`);
      } else {
        alert(data.message || 'Failed');
      }
    } catch (err) { alert('Network error'); }
    finally { setActionLoading(false); }
  };

  // ── Remove Equipment ──
  const handleRemoveEquipment = async (eqId) => {
    if (!window.confirm(lang === 'ar' ? 'إزالة المعدة من العقد؟' : 'Remove equipment from contract?')) return;
    try {
      const res = await fetch(`${API}/contracts/${id}/equipment/${eqId}`, {
        method: 'DELETE', headers
      });
      if (res.ok) fetchEquipment();
    } catch (err) { console.error(err); }
  };

  // ── Add Equipment Modal ──
  const openAddEquipment = async () => {
    if (!contract?.client_id) return;
    try {
      const res = await fetch(`${API}/equipment?client_id=${contract.client_id}&limit=500`, { headers });
      if (res.ok) {
        const data = await res.json();
        const existingIds = equipment.map(e => e.equipment_id || e.id);
        setAvailableEquipment((data.data || []).filter(e => !existingIds.includes(e.id)));
      }
    } catch (err) { console.error(err); }
    setSelectedNewEquip([]);
    setShowAddEquip(true);
  };

  const handleAddEquipment = async () => {
    for (const eqId of selectedNewEquip) {
      try {
        await fetch(`${API}/contracts/${id}/equipment`, {
          method: 'POST', headers: jsonHeaders,
          body: JSON.stringify({ equipment_id: eqId })
        });
      } catch (err) { console.error(err); }
    }
    setShowAddEquip(false);
    fetchEquipment();
  };

  // ── Status Action Buttons ──
  const getStatusActions = (status) => {
    const map = {
      draft: ['active', 'cancelled'],
      active: ['suspended', 'cancelled'],
      suspended: ['active', 'cancelled'],
      expired: [],
      cancelled: [],
      renewed: [],
    };
    return map[status] || [];
  };

  const canRenew = (status) => ['active', 'expired'].includes(status);

  // ── InfoRow ──
  const InfoRow = ({ label, value, highlight }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
      <span style={{ color: '#6b7280', fontSize: 14 }}>{label}</span>
      <span style={{ fontWeight: highlight ? 700 : 500, fontSize: 14, color: highlight ? '#111827' : '#374151' }}>{value}</span>
    </div>
  );

  // ── Progress Bar for Timeline ──
  const TimelineProgress = () => {
    if (!contract?.start_date || !contract?.end_date) return null;
    const start = new Date(contract.start_date).getTime();
    const end = new Date(contract.end_date).getTime();
    const now = Date.now();
    const total = end - start;
    const elapsed = now - start;
    const pct = total > 0 ? Math.min(100, Math.max(0, (elapsed / total) * 100)) : 0;
    const daysLeft = Math.max(0, Math.ceil((end - now) / (1000 * 60 * 60 * 24)));
    const isExpired = now > end;

    return (
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#6b7280', marginBottom: 6 }}>
          <span>{fmtDate(contract.start_date)}</span>
          <span style={{ fontWeight: 600, color: isExpired ? '#dc2626' : '#2563eb' }}>
            {isExpired
              ? (f.ct_expired_label || 'Expired')
              : (lang === 'ar' ? `${daysLeft} يوم متبقي` : `${daysLeft} days left`)}
          </span>
          <span>{fmtDate(contract.end_date)}</span>
        </div>
        <div style={{ height: 8, borderRadius: 4, background: '#e5e7eb', overflow: 'hidden' }}>
          <div style={{
            height: '100%', borderRadius: 4, transition: 'width 0.5s',
            width: `${pct}%`,
            background: isExpired ? '#dc2626' : pct > 80 ? '#d97706' : '#2563eb'
          }} />
        </div>
      </div>
    );
  };


  // Fetch work orders for this contract
  const fetchContractWOs = async () => {
    if (!id) return;
    try {
      setWoLoading(true);
      const res = await fetch(API + '/work-orders?contract_id=' + id + '&limit=50', { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } });
      const data = await res.json();
      if (data.success) setContractWOs(data.data || []);
    } catch (err) { console.error(err); }
    finally { setWoLoading(false); }
  };

  useEffect(() => { if (activeTab === 'work_orders') fetchContractWOs(); }, [activeTab, id]);
  if (loading) {
    return (
      <div style={{ direction: isRTL ? 'rtl' : 'ltr', padding: 60, textAlign: 'center', color: '#6b7280' }}>
        {f.loading || 'Loading...'}
      </div>
    );
  }

  if (!contract) {
    return (
      <div style={{ direction: isRTL ? 'rtl' : 'ltr', padding: 60, textAlign: 'center' }}>
        <p>{f.ct_not_found || 'Contract not found'}</p>
        <button onClick={() => navigate('/fsm/contracts')} style={{ marginTop: 12, padding: '8px 16px', borderRadius: 8, border: '1px solid #6b7280', background: 'transparent', cursor: 'pointer' }}>
          {f.back_to_contracts || 'Back to Contracts'}
        </button>
      </div>
    );
  }

  const sc = statusColors[contract.status] || statusColors.draft;
  const tabs = [
    { id: 'overview', label: f.ct_tab_overview || 'Overview', icon: '📋' },
    { id: 'equipment', label: f.ct_tab_equipment || 'Equipment', icon: '⚙️', count: equipment.length },
    { id: 'history', label: f.ct_tab_history || 'History', icon: '🔄' },
    { id: 'maintenance', label: f.ct_tab_maintenance || 'Maintenance Plans', icon: '🔧', count: maintenancePlans.length },
    { id: 'work_orders', label: f.fsm_work_orders_tab || f.fsm_work_orders || 'أوامر العمل', icon: '🔧', count: contractWOs.length },
  ];

  return (
    <Layout>
    <div style={{ direction: isRTL ? 'rtl' : 'ltr', padding: '24px', fontFamily: 'inherit' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <button
            onClick={() => navigate('/fsm/contracts')}
            style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 13, padding: 0, opacity: 0.7, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}
          >
            {isRTL ? '→' : '←'} {f.back_to_contracts || 'Back to Contracts'}
          </button>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 12 }}>
            {contract.contract_number}
            <span style={{
              padding: '4px 12px', borderRadius: 20, fontSize: 13,
              fontWeight: 600, background: sc.bg, color: sc.color
            }}>
              {sc.icon} {statusLabel(contract.status)}
            </span>
          </h1>
          <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 14 }}>
            {lang === 'ar' ? (contract.client_name_ar || contract.client_name) : contract.client_name}
            {contract.type && ` · ${typeLabel(contract.contract_type)}`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {getStatusActions(contract.status).map(action => (
            <button
              key={action}
              onClick={() => handleStatusChange(action)}
              disabled={actionLoading}
              style={{
                padding: '8px 16px', borderRadius: 8, border: '1px solid #6b7280',
                background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 500,
                color: action === 'cancelled' ? '#dc2626' : '#374151'
              }}
            >
              {statusLabel(action)}
            </button>
          ))}
          {canRenew(contract.status) && (
            <button
              onClick={handleRenew}
              disabled={actionLoading}
              style={{
                padding: '8px 16px', borderRadius: 8, border: 'none',
                background: '#7c3aed', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600
              }}
            >
              🔄 {f.ct_renew || 'Renew'}
            </button>
          )}
          <button
            onClick={() => navigate(`/fsm/contracts/${id}/edit`)}
            style={{
              padding: '8px 16px', borderRadius: 8, border: 'none',
              background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600
            }}
          >
            ✏️ {f.edit || 'Edit'}
          </button>
          <GenerateContractPDF contractId={contract.id} contractNumber={contract.contract_number} />
        </div>
      </div>

      {/* Timeline Progress */}
      <TimelineProgress />

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid #e5e7eb' }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: 14, fontWeight: activeTab === tab.id ? 600 : 400,
              color: activeTab === tab.id ? '#2563eb' : '#6b7280',
              borderBottom: activeTab === tab.id ? '2px solid #2563eb' : '2px solid transparent',
              marginBottom: -2, display: 'flex', alignItems: 'center', gap: 6
            }}
          >
            {tab.icon} {tab.label}
            {tab.count !== undefined && (
              <span style={{
                background: activeTab === tab.id ? '#dbeafe' : '#f3f4f6',
                color: activeTab === tab.id ? '#2563eb' : '#6b7280',
                padding: '2px 8px', borderRadius: 10, fontSize: 12, fontWeight: 600
              }}>{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20 }}>
          {/* Contract Details */}
          <div style={{ background: 'transparent', borderRadius: 12, border: '1px solid #4b5563', padding: 24 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>
              📋 {f.ct_details || 'Contract Details'}
            </h3>
            <InfoRow label={f.ct_number || 'Contract #'} value={contract.contract_number} highlight />
            <InfoRow label={f.ct_type || 'Type'} value={typeLabel(contract.contract_type)} />
            <InfoRow label={f.ct_priority || 'Priority'} value={f[`pri_${contract.priority_level || contract.coverage_scope}`] || contract.priority_level || contract.coverage_scope} />
            <InfoRow label={f.ct_start || 'Start'} value={fmtDate(contract.start_date)} />
            <InfoRow label={f.ct_end || 'End'} value={fmtDate(contract.end_date)} />
            <InfoRow label={f.ct_auto_renew || 'Auto-Renew'} value={contract.auto_renew ? '✅' : '—'} />
            <InfoRow label={f.ct_coverage || 'Coverage'} value={f[`cov_${contract.coverage_scope}`] || contract.coverage_scope} />
          </div>

          {/* SLA & Financial */}
          <div>
            {/* SLA Card */}
            <div style={{ background: 'transparent', borderRadius: 12, border: '1px solid #4b5563', padding: 24, marginBottom: 20 }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>
                ⏱️ {f.ct_sla_terms || 'SLA Terms'}
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={{ padding: 16, borderRadius: 8, background: 'rgba(37,99,235,0.12)', textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#2563eb' }}>
                    {contract.sla_response_hours || '—'}h
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                    {f.ct_response_time || 'Response Time'}
                  </div>
                </div>
                <div style={{ padding: 16, borderRadius: 8, background: 'rgba(217,119,6,0.12)', textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#d97706' }}>
                    {contract.sla_resolution_hours || '—'}h
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                    {f.ct_resolution_time || 'Resolution Time'}
                  </div>
                </div>
              </div>
              {contract.total_visits_allowed && (
                <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: 'rgba(22,163,106,0.12)', textAlign: 'center' }}>
                  <span style={{ fontWeight: 700, color: '#16a34a', fontSize: 18 }}>{contract.total_visits_allowed}</span>
                  <span style={{ color: '#6b7280', fontSize: 13, marginLeft: 8 }}>
                    {f.ct_included_visits || 'Included Visits'}
                  </span>
                </div>
              )}
            </div>

            {/* Financial Card */}
            <div style={{ background: 'transparent', borderRadius: 12, border: '1px solid #4b5563', padding: 24 }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>
                💰 {f.ct_financial || 'Financial'}
              </h3>
              <InfoRow label={f.ct_value || 'Value'} value={`${fmtCurrency(contract.contract_value)}`} highlight />
              <InfoRow label={f.ct_billing || 'Billing'} value={f[`freq_${contract.visit_frequency}`] || contract.visit_frequency} />
              <InfoRow label={f.ct_payment_terms || 'Payment Terms'} value={`${contract.payment_terms || 30} ${f.days || 'days'}`} />
              {contract.cost_incurred > 0 && (
                <InfoRow label={f.ct_cost_incurred || 'Cost Incurred'} value={`${fmtCurrency(contract.cost_incurred)}`} />
              )}
            </div>
          </div>

          {/* Notes */}
          {(contract.terms_conditions || contract.notes) && (
            <div style={{ background: 'transparent', borderRadius: 12, border: '1px solid #4b5563', padding: 24, gridColumn: '1 / -1' }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>
                📝 {f.ct_terms_notes || 'Terms & Notes'}
              </h3>
              {contract.terms_conditions && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 4 }}>{f.ct_terms || 'Terms & Conditions'}</div>
                  <p style={{ margin: 0, fontSize: 14, whiteSpace: 'pre-wrap', color: '#374151' }}>{contract.terms_conditions}</p>
                </div>
              )}
              {contract.notes && (
                <div>
                  <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 4 }}>{f.ct_notes || 'Internal Notes'}</div>
                  <p style={{ margin: 0, fontSize: 14, whiteSpace: 'pre-wrap', color: '#374151' }}>{contract.notes}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'equipment' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
              {f.ct_contract_equipment || 'Contract Equipment'} ({equipment.length})
            </h3>
            <button
              onClick={openAddEquipment}
              style={{
                padding: '8px 16px', borderRadius: 8, border: 'none',
                background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600
              }}
            >
              + {f.add_equipment || 'Add Equipment'}
            </button>
          </div>

          {equipment.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', background: 'transparent', borderRadius: 12, border: '1px solid #4b5563' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>⚙️</div>
              <p>{f.ct_no_equipment || 'No equipment linked to this contract'}</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
              {equipment.map(eq => (
                <div key={eq.id || eq.equipment_id} style={{
                  background: 'transparent', borderRadius: 12, border: '1px solid #4b5563', padding: 16
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 15 }}>
                        {lang === 'ar' ? (eq.name_ar || eq.equipment_name || eq.name) : (eq.equipment_name || eq.name)}
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                        {eq.serial_number && <span>{f.serial || 'S/N'}: {eq.serial_number}</span>}
                        {eq.brand && <span> · {eq.brand} {eq.model || ''}</span>}
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveEquipment(eq.equipment_id || eq.id)}
                      style={{
                        background: 'none', border: 'none', color: '#dc2626',
                        cursor: 'pointer', fontSize: 16, padding: 4
                      }}
                      title={f.remove || 'Remove'}
                    >
                      ✕
                    </button>
                  </div>
                  {eq.coverage_type && (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#2563eb' }}>
                      {f.ct_coverage || 'Coverage'}: {f[`cov_${eq.coverage_type}`] || eq.coverage_type}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Add Equipment Modal */}
          {showAddEquip && (
            <div style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 1000
            }} onClick={() => setShowAddEquip(false)}>
              <div
                onClick={e => e.stopPropagation()}
                style={{
                  background: '#1e293b', borderRadius: 16, padding: 24,
                  width: '90%', maxWidth: 500, maxHeight: '70vh', overflow: 'auto',
                  direction: isRTL ? 'rtl' : 'ltr'
                }}
              >
                <h3 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 600 }}>
                  {f.add_equipment || 'Add Equipment'}
                </h3>
                {availableEquipment.length === 0 ? (
                  <p style={{ color: '#6b7280' }}>{f.ct_all_added || 'All client equipment is already in this contract'}</p>
                ) : (
                  <>
                    {availableEquipment.map(eq => {
                      const sel = selectedNewEquip.includes(eq.id);
                      return (
                        <div
                          key={eq.id}
                          onClick={() => setSelectedNewEquip(prev =>
                            sel ? prev.filter(x => x !== eq.id) : [...prev, eq.id]
                          )}
                          style={{
                            padding: 12, borderRadius: 8, cursor: 'pointer', marginBottom: 8,
                            border: `2px solid ${sel ? '#2563eb' : '#4b5563'}`,
                            background: sel ? 'rgba(37,99,235,0.15)' : 'transparent'
                          }}
                        >
                          <div style={{ fontWeight: 600, fontSize: 14 }}>
                            {lang === 'ar' ? (eq.name_ar || eq.name) : eq.name}
                          </div>
                          <div style={{ fontSize: 12, color: '#6b7280' }}>
                            {eq.serial_number || ''} {eq.brand || ''}
                          </div>
                        </div>
                      );
                    })}
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
                      <button
                        onClick={() => setShowAddEquip(false)}
                        style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #6b7280', background: 'transparent', cursor: 'pointer' }}
                      >
                        {f.cancel || 'Cancel'}
                      </button>
                      <button
                        onClick={handleAddEquipment}
                        disabled={selectedNewEquip.length === 0}
                        style={{
                          padding: '8px 16px', borderRadius: 8, border: 'none',
                          background: selectedNewEquip.length > 0 ? '#2563eb' : '#93c5fd',
                          color: '#fff', cursor: selectedNewEquip.length > 0 ? 'pointer' : 'default',
                          fontWeight: 600
                        }}
                      >
                        {f.add || 'Add'} ({selectedNewEquip.length})
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'history' && (
        <div style={{ background: 'transparent', borderRadius: 12, border: '1px solid #4b5563', padding: 24 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>
            🔄 {f.ct_renewal_history || 'Renewal History'}
          </h3>
          {contract.renewal_chain && contract.renewal_chain.length > 0 ? (
            <div style={{ position: 'relative' }}>
              {contract.renewal_chain.map((r, idx) => {
                const rsc = statusColors[r.status] || statusColors.draft;
                return (
                  <div key={r.id} style={{
                    display: 'flex', gap: 16, alignItems: 'flex-start', marginBottom: 16,
                    paddingBottom: 16, borderBottom: idx < contract.renewal_chain.length - 1 ? '1px solid #f3f4f6' : 'none'
                  }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%', background: rsc.bg,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 16, flexShrink: 0
                    }}>
                      {rsc.icon}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{ fontWeight: 600, color: '#2563eb', cursor: 'pointer', fontSize: 14 }}
                        onClick={() => navigate(`/fsm/contracts/${r.id}`)}
                      >
                        {r.contract_number}
                      </div>
                      <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
                        {fmtDate(r.start_date)} → {fmtDate(r.end_date)}
                        <span style={{
                          marginLeft: 8, marginRight: 8, padding: '2px 8px', borderRadius: 10,
                          fontSize: 11, background: rsc.bg, color: rsc.color, fontWeight: 600
                        }}>
                          {statusLabel(r.status)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p style={{ color: '#6b7280', fontSize: 14 }}>
              {f.ct_no_history || 'No renewal history'}
            </p>
          )}
        </div>
      )}


      {/* Work Orders Tab */}
      {activeTab === 'work_orders' && (
        <div style={{ background: 'transparent', borderRadius: 12, border: '1px solid #4b5563', padding: 24 }}>
          {woLoading ? <div style={{ textAlign: 'center', padding: '30px', color: '#6b7280' }}>Loading...</div> :
          contractWOs.length === 0 ? <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>لا توجد أوامر عمل مرتبطة بهذا العقد</div> : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>WO #</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>Title</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>Type</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>Priority</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>Status</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>Scheduled</th>
                </tr></thead>
                <tbody>
                  {contractWOs.map(wo => {
                    const woStatusColors = { draft:'#6b7280', open:'#3b82f6', assigned:'#8b5cf6', in_progress:'#f59e0b', on_hold:'#ef4444', completed:'#10b981', closed:'#6b7280', cancelled:'#991b1b' };
                    const priColors = { critical:'#dc2626', high:'#f97316', medium:'#eab308', low:'#22c55e' };
                    return (
                      <tr key={wo.id} style={{ cursor: 'pointer' }} onClick={() => navigate('/fsm/work-orders/' + wo.id)}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.02)'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                        <td style={{ padding: '10px 12px', fontSize: 13, color: '#2563eb', fontWeight: 600, borderBottom: '1px solid #f3f4f6' }}>{wo.wo_number}</td>
                        <td style={{ padding: '10px 12px', fontSize: 13, borderBottom: '1px solid #f3f4f6' }}>{wo.title}</td>
                        <td style={{ padding: '10px 12px', fontSize: 13, borderBottom: '1px solid #f3f4f6' }}>{wo.wo_type}</td>
                        <td style={{ padding: '10px 12px', fontSize: 13, borderBottom: '1px solid #f3f4f6' }}><span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, backgroundColor: (priColors[wo.priority] || '#6b7280') + '22', color: priColors[wo.priority] || '#6b7280' }}>{wo.priority}</span></td>
                        <td style={{ padding: '10px 12px', fontSize: 13, borderBottom: '1px solid #f3f4f6' }}><span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, backgroundColor: (woStatusColors[wo.status] || '#6b7280') + '22', color: woStatusColors[wo.status] || '#6b7280' }}>{wo.status}</span></td>
                        <td style={{ padding: '10px 12px', fontSize: 13, borderBottom: '1px solid #f3f4f6' }}>{wo.scheduled_date ? new Date(wo.scheduled_date).toLocaleDateString() : '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'maintenance' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
              🔧 {f.ct_maintenance_plans || 'خطط الصيانة'}
              <span style={{ fontSize: 14, fontWeight: 400, color: '#9ca3af', marginInlineStart: 8 }}>
                ({maintenancePlans.length})
              </span>
            </h3>
          </div>
          {maintenancePlans.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {maintenancePlans.map(plan => {
                const isOverdue = plan.next_due_date && new Date(plan.next_due_date) < new Date();
                const isAr = lang === 'ar';
                const freqLabels = { daily: isAr ? 'يومي' : 'Daily', weekly: isAr ? 'أسبوعي' : 'Weekly', biweekly: isAr ? 'نصف شهري' : 'Bi-weekly', monthly: isAr ? 'شهري' : 'Monthly', bimonthly: isAr ? 'كل شهرين' : 'Bi-monthly', quarterly: isAr ? 'ربع سنوي' : 'Quarterly', semi_annual: isAr ? 'نصف سنوي' : 'Semi-Annual', annual: isAr ? 'سنوي' : 'Annual', custom: isAr ? 'مخصص' : 'Custom' };
                const priorityLabels = { low: isAr ? 'منخفضة' : 'Low', medium: isAr ? 'متوسطة' : 'Medium', high: isAr ? 'عالية' : 'High', critical: isAr ? 'حرجة' : 'Critical' };
                const priorityColors = { low: '#60a5fa', medium: '#fbbf24', high: '#fb923c', critical: '#f87171' };
                const planTypeLabels = { time_based: isAr ? 'زمنية' : 'Time-based', frequency_based: isAr ? 'دورية' : 'Frequency-based', condition_based: isAr ? 'حسب الحالة' : 'Condition-based' };
                const statusColor = !plan.is_active ? { bg: '#374151', color: '#9ca3af', label: isAr ? 'متوقفة' : 'Paused' } : isOverdue ? { bg: '#7f1d1d', color: '#fca5a5', label: isAr ? 'متأخرة' : 'Overdue' } : { bg: '#064e3b', color: '#6ee7b7', label: isAr ? 'نشطة' : 'Active' };
                const fmtD = (d) => d ? new Date(d).toLocaleDateString(isAr ? 'ar-EG' : 'en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
                const checklist = plan.checklist ? (typeof plan.checklist === 'string' ? (() => { try { return JSON.parse(plan.checklist); } catch { return []; } })() : plan.checklist) : [];
                const planTitle = isAr ? (plan.name_ar || plan.title_ar || plan.name || plan.title) : (plan.name || plan.title);

                return (
                  <div key={plan.id} style={{
                    border: '1px solid #4b5563', borderRadius: 12, overflow: 'hidden',
                    background: '#111827',
                  }}>
                    {/* ── Header ── */}
                    <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 16, color: '#f3f4f6', marginBottom: 4 }}>
                          {planTitle}
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, fontSize: 12 }}>
                          <span style={{ padding: '2px 10px', borderRadius: 20, background: 'rgba(96,165,250,0.15)', color: '#60a5fa' }}>
                            {freqLabels[plan.recurrence_type || plan.frequency] || plan.recurrence_type || plan.frequency}
                            {plan.recurrence_interval > 1 ? ` × ${plan.recurrence_interval}` : ''}
                          </span>
                          <span style={{ padding: '2px 10px', borderRadius: 20, background: 'rgba(168,85,247,0.15)', color: '#c084fc' }}>
                            {planTypeLabels[plan.plan_type] || plan.plan_type}
                          </span>
                          <span style={{ padding: '2px 10px', borderRadius: 20, background: `${priorityColors[plan.priority] || '#fbbf24'}22`, color: priorityColors[plan.priority] || '#fbbf24' }}>
                            {isAr ? 'الأولوية' : 'Priority'}: {priorityLabels[plan.priority] || plan.priority}
                          </span>
                        </div>
                      </div>
                      <span style={{
                        padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                        background: statusColor.bg, color: statusColor.color, whiteSpace: 'nowrap'
                      }}>
                        {statusColor.label}
                      </span>
                    </div>

                    {/* ── Description ── */}
                    {plan.description && (
                      <div style={{ padding: '12px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', fontSize: 13, color: '#9ca3af', lineHeight: 1.5 }}>
                        {plan.description}
                      </div>
                    )}

                    {/* ── Key Dates & Info Grid ── */}
                    <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 16 }}>
                      <div>
                        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2, textTransform: 'uppercase', fontWeight: 600 }}>
                          {isAr ? 'تاريخ البدء' : 'Start Date'}
                        </div>
                        <div style={{ fontSize: 14, color: '#d1d5db', fontWeight: 500 }}>{fmtD(plan.start_date)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2, textTransform: 'uppercase', fontWeight: 600 }}>
                          {isAr ? 'تاريخ الانتهاء' : 'End Date'}
                        </div>
                        <div style={{ fontSize: 14, color: '#d1d5db', fontWeight: 500 }}>{fmtD(plan.end_date)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2, textTransform: 'uppercase', fontWeight: 600 }}>
                          {isAr ? 'الاستحقاق التالي' : 'Next Due'}
                        </div>
                        <div style={{ fontSize: 14, color: isOverdue ? '#fca5a5' : '#4ade80', fontWeight: 600 }}>{fmtD(plan.next_due_date)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2, textTransform: 'uppercase', fontWeight: 600 }}>
                          {isAr ? 'آخر إنشاء' : 'Last Generated'}
                        </div>
                        <div style={{ fontSize: 14, color: '#d1d5db', fontWeight: 500 }}>{fmtD(plan.last_generated_date)}</div>
                      </div>
                      {plan.estimated_duration_hours && (
                        <div>
                          <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2, textTransform: 'uppercase', fontWeight: 600 }}>
                            {isAr ? 'المدة المقدرة' : 'Est. Duration'}
                          </div>
                          <div style={{ fontSize: 14, color: '#d1d5db', fontWeight: 500 }}>{plan.estimated_duration_hours} {isAr ? 'ساعة' : 'hrs'}</div>
                        </div>
                      )}
                      <div>
                        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2, textTransform: 'uppercase', fontWeight: 600 }}>
                          {isAr ? 'أمر عمل تلقائي' : 'Auto WO'}
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 500 }}>
                          {plan.auto_create_wo ? (
                            <span style={{ color: '#4ade80' }}>✅ {isAr ? 'مفعل' : 'Enabled'} ({plan.advance_days} {isAr ? 'يوم مقدماً' : 'days ahead'})</span>
                          ) : (
                            <span style={{ color: '#9ca3af' }}>❌ {isAr ? 'معطل' : 'Disabled'}</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2, textTransform: 'uppercase', fontWeight: 600 }}>
                          {isAr ? 'المعدات' : 'Equipment'}
                        </div>
                        <div style={{ fontSize: 14, color: '#d1d5db', fontWeight: 500 }}>
                          ⚙️ {plan.equipment_count || 0} {isAr ? 'معدة مرتبطة' : 'linked'}
                        </div>
                      </div>
                    </div>

                    {/* ── Checklist ── */}
                    {checklist.length > 0 && (
                      <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#9ca3af', marginBottom: 10, textTransform: 'uppercase' }}>
                          📋 {isAr ? 'قائمة الفحص' : 'Checklist'} ({checklist.length})
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {checklist.map((ci, idx) => (
                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#d1d5db' }}>
                              <span style={{ color: '#6b7280', fontSize: 16 }}>☐</span>
                              <span>{ci.text || ci.item || ci}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* ── Instructions ── */}
                    {(plan.instructions || plan.instructions_ar) && (
                      <div style={{ padding: '16px 20px' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#9ca3af', marginBottom: 10, textTransform: 'uppercase' }}>
                          📝 {isAr ? 'التعليمات' : 'Instructions'}
                        </div>
                        <div style={{ fontSize: 13, color: '#d1d5db', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                          {isAr ? (plan.instructions_ar || plan.instructions) : (plan.instructions || plan.instructions_ar)}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ background: 'transparent', borderRadius: 12, border: '1px solid #4b5563', padding: 40, textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🔧</div>
              <p style={{ color: '#6b7280', fontSize: 14, margin: 0 }}>
                {f.ct_no_maintenance || 'No maintenance plans linked to this contract.'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
    </Layout>
  );
};

export default ContractDetail;
